import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal, FadeUp } from "@/components/motion";
import EditorialMarquee from "@/components/EditorialMarquee";
import { PRODUCTS } from "@/data/content";

function ProductsHero() {
  return (
    <section data-testid="products-hero" className="pt-44 pb-20 sm:pb-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <FadeUp delay={0.1}>
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand">Our Products</p>
        </FadeUp>
        <MaskedLines
          className="mt-8 font-display font-medium uppercase tracking-tighter leading-[0.95] text-[13vw] sm:text-[9vw] lg:text-[7rem] text-ink"
          delay={0.25}
          lines={[
            "Commodities",
            <span key="amp">
              <span className="text-outline-brand">&</span> Trading<span className="text-brand">.</span>
            </span>,
          ]}
        />
        <FadeUp delay={0.9} className="mt-10 max-w-xl">
          <p className="text-black/65 font-light leading-relaxed text-base sm:text-lg">
            A focused portfolio of energy products — sourced globally, traded with
            discipline, delivered to specification.
          </p>
        </FadeUp>
      </div>
    </section>
  );
}

function ProductRow({ product, index }) {
  const even = index % 2 === 0;
  return (
    <Reveal>
      <article
        data-testid={`product-row-${product.code.toLowerCase()}`}
        className="border-t border-black/10 py-14 sm:py-20 grid md:grid-cols-12 gap-10 items-center"
      >
        <div className={`md:col-span-6 ${even ? "" : "md:order-2"}`}>
          <div className="group relative overflow-hidden aspect-[4/3] bg-mist">
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/45 to-transparent" />
            <p className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.3em] text-white">
              {product.code}
            </p>
          </div>
        </div>
        <div className={`md:col-span-6 ${even ? "" : "md:order-1"}`}>
          <h2 className="font-display font-medium uppercase tracking-tight text-4xl sm:text-5xl text-ink">
            {product.name}
          </h2>
          <p className="mt-6 text-black/60 font-light leading-relaxed max-w-lg">{product.desc}</p>
          <dl className="mt-10 border-t border-black/10">
            {product.specs.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6 py-4 border-b border-black/10">
                <dt className="font-mono text-[10px] tracking-[0.25em] uppercase text-black/40">{k}</dt>
                <dd className="font-mono text-[11px] tracking-[0.15em] uppercase text-black/75 text-right">{v}</dd>
              </div>
            ))}
          </dl>
          <Link
            to="/contact"
            data-testid={`product-enquire-${product.code.toLowerCase()}`}
            className="mt-8 group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-ink hover:text-brand transition-colors duration-300"
          >
            Enquire About {product.name}
            <ArrowUpRight className="w-4 h-4 text-brand transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </article>
    </Reveal>
  );
}

function BespokeCard() {
  return (
    <Reveal>
      <div
        data-testid="product-bespoke"
        className="border-t border-black/10 py-14 sm:py-20 grid md:grid-cols-12 gap-10 items-center"
      >
        <div className="md:col-span-7">
          <p className="font-mono text-[10px] tracking-[0.3em] text-brand">P-05</p>
          <h2 className="mt-4 font-display font-medium uppercase tracking-tight text-4xl sm:text-5xl text-ink">
            And more<span className="text-brand">.</span>
          </h2>
          <p className="mt-6 text-black/60 font-light leading-relaxed max-w-lg">
            Beyond our core portfolio, we source specialty petroleum products on request.
            Tell us your specification — our trading desk will structure the supply.
          </p>
        </div>
        <div className="md:col-span-5 md:justify-self-end">
          <Link
            to="/contact"
            data-testid="bespoke-cta"
            className="group inline-flex items-center gap-2 bg-brand text-white px-8 py-4 font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-brand-hover transition-colors duration-300"
          >
            Request a Product
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </Reveal>
  );
}

export default function Products() {
  return (
    <div data-testid="products-page">
      <ProductsHero />
      <EditorialMarquee />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-10">
        {PRODUCTS.map((p, i) => (
          <ProductRow key={p.code} product={p} index={i} />
        ))}
        <BespokeCard />
      </div>
    </div>
  );
}
